import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import {
  DataGridPro,
  GridColumns,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid-pro'
import { GridCsvExportOptions } from '@mui/x-data-grid'
import ConfirmDialog from '@components/dialogs/ConfirmDialog'
import { UpTransition } from '@components/animations'
import {
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOffOutlined as ToggleOffOutlinedIcon,
} from '@mui/icons-material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { capitalize } from 'lodash'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EventLogsTable from './EventLogsTable'
import { EditIcon } from 'lucide-react'

type ResolvedAction = {
  key: string
  label: string
  icon: React.ReactNode
  disabled?: boolean
  disabledTooltip?: string
  onClick: () => void
}

function ActionsMenu({ items }: { items: ResolvedAction[] }) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const close = () => setAnchorEl(null)
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget) }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close} onClick={(e) => e.stopPropagation()}>
        {items.map((item) => (
          <MenuItem key={item.key} disabled={item.disabled} onClick={() => { close(); item.onClick() }}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

type Action = 'edit' | 'viewLogs' | 'toggle' | 'delete'

export type RowBase = {
  id: string
  isActive?: boolean
  isDefaultValue?: boolean
}

export interface BaseConfigTableProps<T extends RowBase> {
  columns: GridColumns<T>
  selectRows: (state: any) => {
    datas: T[]
    totalCount: number
  }
  fetchThunk: () => any
  deleteThunk?: (id: string) => any
  toggleThunk?: (id: string, isActive: boolean) => any
  updatePageThunk: (page: number) => any
  FormComponent?: React.ComponentType<{
    editingItem: T | null
    onClose: () => void
  }>
  resourceLabel?: string
  resourcePrefix?: string
  extraActions?:
    | {
        key: string
        label: string
        icon: React.ReactNode
        disabled?: boolean | ((row: T) => boolean)
        disabledTooltip?: string
        onClick: (row: T) => void
      }[]
    | ((row: T) => {
        key: string
        label: string
        icon: React.ReactNode
        disabled?: boolean | ((row: T) => boolean)
        disabledTooltip?: string
        onClick: (row: T) => void
      }[])
  menuActions?:
    | {
        key: string
        label: string
        icon: React.ReactNode
        disabled?: boolean | ((row: T) => boolean)
        disabledTooltip?: string
        onClick: (row: T) => void
      }[]
    | ((row: T) => {
        key: string
        label: string
        icon: React.ReactNode
        disabled?: boolean | ((row: T) => boolean)
        disabledTooltip?: string
        onClick: (row: T) => void
      }[])
  extraWidth?: number
  formMaxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  toolbarExtras?: React.ReactNode
  toolbarExtrasPosition?: 'left' | 'right'
  exportCsvOptions?: GridCsvExportOptions
  disabledActions?: Action[]
  disabledEdit?: boolean
  removeToolbar?: boolean
  withActions?: boolean
}

export default function BaseConfigTable<T extends RowBase>({
  columns,
  selectRows,
  fetchThunk,
  deleteThunk,
  toggleThunk,
  updatePageThunk,
  FormComponent,
  resourceLabel = 'élément',
  resourcePrefix = 'l\'',
  extraActions,
  menuActions,
  extraWidth,
  formMaxWidth = 'sm',
  toolbarExtras,
  toolbarExtrasPosition = 'right',
  exportCsvOptions,
  disabledActions,
  disabledEdit,
  removeToolbar,
  withActions = true,
}: BaseConfigTableProps<T>) {
  const dispatch = useAppDispatch()
  const rows = useAppSelector(selectRows)
  const [loading, setLoading] = useState(true)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [eventLogsTableOpen, setEventLogsTableOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<T | null>(null)
  const [confirmFor, setConfirmFor] = useState<{
    action: 'delete' | 'toggle'
    row: T
  } | null>(null)

  useEffect(() => {
    dispatch(fetchThunk()).finally(() => setLoading(false))
  }, [])

  const resourceFullLabel = `${resourcePrefix}${resourceLabel}`

  const actionColumn = useMemo(
    () => ({
      field: '__actions__',
      headerName: 'Actions',
      disableExport: true,
      width: menuActions
        ? (extraWidth || 0) + 55
        : 170 +
          (extraActions?.length ? extraActions.length * 35 : 0) +
          (extraWidth || 0) +
          (toggleThunk ? 0 : -35),
      sortable: false,
      renderCell: (params: any) => {
        const row = params.row as T

        const resolveExtra = (src: typeof extraActions): ResolvedAction[] =>
          (typeof src === 'function' ? src(row) : src || []).map((a) => ({
            ...a,
            disabled: typeof a.disabled === 'function' ? a.disabled(row) : a.disabled,
            disabledTooltip: a.disabledTooltip,
            onClick: () => a.onClick(row),
          }))

        const builtInActions: ResolvedAction[] = [
          ...(!disabledEdit
            ? [{
                key: 'edit',
                label: 'Modifier',
                icon: <EditIcon />,
                disabled: !!(row.isDefaultValue || disabledActions?.includes('edit')),
                onClick: () => { if (!row.isDefaultValue) { setSelectedItem(row); setIsFormOpen(true) } },
              }]
            : []),
          {
            key: 'viewLogs',
            label: 'Voir les logs de configuration',
            icon: <OpenInNewIcon color="primary" />,
            disabled: !!disabledActions?.includes('viewLogs'),
            onClick: () => { setSelectedItem(row); setEventLogsTableOpen(true) },
          },
          ...(toggleThunk
            ? [{
                key: 'toggle',
                label: row.isActive ? 'Désactiver' : 'Activer',
                icon: row.isActive
                  ? <ToggleOnIcon fontSize="small" color="primary" />
                  : <ToggleOffOutlinedIcon fontSize="small" color="primary" />,
                disabled: !!disabledActions?.includes('toggle'),
                onClick: () => setConfirmFor({ action: 'toggle', row }),
              }]
            : []),
          ...(deleteThunk
            ? [{
                key: 'delete',
                label: 'Supprimer',
                icon: <DeleteIcon color="primary" />,
                disabled: !!(row.isDefaultValue || disabledActions?.includes('delete')),
                onClick: () => { if (!row.isDefaultValue) setConfirmFor({ action: 'delete', row }) },
              }]
            : []),
        ]

        if (menuActions) {
          const standaloneActions = resolveExtra(extraActions)
          const menuItems = [...builtInActions, ...resolveExtra(menuActions)]
          return (
            <div className="flex items-center space-x-1">
              {standaloneActions.map((a) => (
                <Tooltip key={a.key} title={a.disabled && a.disabledTooltip ? a.disabledTooltip : a.label}>
                  <span style={{ display: 'inline-flex' }}>
                    <IconButton
                      size="small"
                      disabled={a.disabled}
                      onClick={(e) => { e.stopPropagation(); a.onClick() }}
                    >
                      {a.icon}
                    </IconButton>
                  </span>
                </Tooltip>
              ))}
              <ActionsMenu items={menuItems} />
            </div>
          )
        }

        const allActions = [...builtInActions, ...resolveExtra(extraActions)]
        return (
          <div className="flex space-x-2">
            {allActions.map((a) => (
              <Tooltip key={a.key} title={a.label}>
                <div
                  className={`cursor-pointer p-1 hover:bg-gray-200 rounded ${
                    a.disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  onClick={(e) => { e.stopPropagation(); if (!a.disabled) a.onClick() }}
                >
                  {a.icon}
                </div>
              </Tooltip>
            ))}
          </div>
        )
      },
    }),
    [],
  )

  const mergedColumns = useMemo(
    () => (withActions ? [actionColumn, ...columns] : columns),
    [actionColumn, columns, withActions],
  )

  const Toolbar = () => (
    <GridToolbarContainer>
      <div className="ml-2 mt-2 flex items-center space-x-2">
        {toolbarExtrasPosition === 'left' && toolbarExtras}
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            setSelectedItem(null)
            setIsFormOpen(true)
          }}
        >
          <i className="fas fa-plus" /> <span className="ml-2">Ajouter</span>
        </Button>
        {toolbarExtrasPosition === 'right' && toolbarExtras}
        {exportCsvOptions && <GridToolbarExport csvOptions={exportCsvOptions} />}
      </div>
    </GridToolbarContainer>
  )

  return (
    <Container maxWidth={false} style={{ height: '100%', padding: '0px 30px' }}>
      <DataGridPro
        density="standard"
        pagination
        pageSize={20}
        rowsPerPageOptions={[20]}
        paginationMode="server"
        style={{
          height: 'calc(100vh - 198px)',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '15px 15px 25px 25px',
        }}
        components={{ Toolbar: removeToolbar ? () => <></> : Toolbar }}
        columns={mergedColumns as any}
        rows={rows.datas}
        rowCount={rows.totalCount}
        loading={loading}
        disableSelectionOnClick
        disableMultipleSelection
        getRowClassName={(params) =>
          !toggleThunk || params.row.isActive ? '' : 'text-gray-400'
        }
        onPageChange={(page) => {
          dispatch(updatePageThunk(page))
        }}
        sx={{
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f8f8f8',
          },
        }}
      />

      <Dialog
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
        maxWidth={formMaxWidth}
        fullWidth={!!formMaxWidth}
      >
        <DialogContent>
          {isFormOpen && FormComponent && (
            <FormComponent
              editingItem={selectedItem}
              onClose={() => {
                setIsFormOpen(false)
                setSelectedItem(null)
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={eventLogsTableOpen}
        onClose={() => setEventLogsTableOpen(false)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent
          style={{
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {selectedItem && (
            <>
              <div className="flex justify-end mb-2">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setEventLogsTableOpen(false)}
                >
                  Fermer
                </Button>
              </div>
              <EventLogsTable entityId={selectedItem.id} />
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        title={
          confirmFor?.action === 'delete'
            ? `Suppression ${resourceLabel}`
            : confirmFor?.action === 'toggle'
            ? `${
                confirmFor.row.isActive ? 'Désactivation' : 'Activation'
              } ${resourceLabel}`
            : ''
        }
        message={
          confirmFor?.action === 'delete'
            ? `Voulez-vous vraiment supprimer ${resourceFullLabel} ?`
            : confirmFor?.action === 'toggle'
            ? `Voulez-vous vraiment ${
                confirmFor.row.isActive ? 'désactiver' : 'activer'
              } ${resourceFullLabel} ?`
            : ''
        }
        open={!!confirmFor}
        onConfirm={async () => {
          if (!confirmFor) return
          if (confirmFor.action === 'delete' && deleteThunk) {
            await dispatch(deleteThunk(confirmFor.row.id)).unwrap()
            dispatch(
              enqueueSnackbar({
                message: `${capitalize(
                  resourceFullLabel,
                )} a été supprimé(e) avec succès`,
                options: { variant: 'success' },
              }),
            )
          }
          if (confirmFor.action === 'toggle' && toggleThunk) {
            await dispatch(
              toggleThunk(confirmFor.row.id, !confirmFor.row.isActive),
            ).unwrap()
            dispatch(
              enqueueSnackbar({
                message: `${capitalize(resourceFullLabel)} a été ${
                  confirmFor.row.isActive ? 'désactivé(e)' : 'activé(e)'
                } avec succès`,
                options: { variant: 'success' },
              }),
            )
          }
          setConfirmFor(null)
        }}
        onCancel={() => setConfirmFor(null)}
      />
    </Container>
  )
}
